/**
 * ESRS E2 · Pollution — facility pollutant recording plus E2-4 (air/water
 * pollutants) and E2-5 (substances of concern) disclosure.
 *
 * Backend: backend/routes/esrs_e2_routes.py — GET /disclosure, /records,
 * /limits; POST /air-emissions, /water-discharges, /substances. Field names,
 * enums, and response shapes are taken verbatim from that route + its service.
 * Chrome comes entirely from components/esg/EsgKit.jsx (CBAM idiom).
 */
import { useCallback, useEffect, useState } from "react";
import {
  Factory, Wind, Droplets, FlaskConical, ListChecks, Plus, X,
  ShieldCheck, ShieldAlert, AlertTriangle, Recycle, Ban, Scale,
} from "lucide-react";
import { useEntities } from "../../../hooks/useEntities";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, TabBar,
  DataTable, EmptyState, ErrorBanner, Skeleton, field, Label, DataQualityBadge,
  apiFetch, fmt, C, MONO, SANS,
} from "../../../components/esg/EsgKit";

// ── Enums / reference vocab (verbatim from esrs_e2_service.py) ────────────────
const AIR_POLLUTANTS = [
  { key: "sox_kg", label: "SOx" },
  { key: "nox_kg", label: "NOx" },
  { key: "pm10_kg", label: "PM10" },
  { key: "pm25_kg", label: "PM2.5" },
  { key: "voc_kg", label: "VOC" },
  { key: "co_kg", label: "CO" },
  { key: "ammonia_kg", label: "Ammonia" },
  { key: "heavy_metals_kg", label: "Heavy metals" },
  { key: "ozone_depleting_kg", label: "Ozone-depleting" },
];

const MEASUREMENT_METHODS = ["measured", "calculated", "estimated"];

// route comment: "river | sea | municipal_treatment | ..."
const DISCHARGE_DESTINATIONS = [
  { value: "river", label: "River" },
  { value: "sea", label: "Sea" },
  { value: "lake", label: "Lake" },
  { value: "groundwater", label: "Groundwater" },
  { value: "municipal_treatment", label: "Municipal treatment" },
];

// SubstanceCategory enum values from services/esrs_e2_service.py
const SUBSTANCE_CATEGORIES = [
  { value: "svhc", label: "SVHC — very high concern (REACH)" },
  { value: "soc", label: "SOC — substance of concern" },
  { value: "pop", label: "POP — persistent organic pollutant" },
  { value: "ods", label: "ODS — ozone-depleting substance" },
  { value: "cmr", label: "CMR — carcinogenic/mutagenic/reprotoxic" },
];
const CATEGORY_LABEL = Object.fromEntries(
  SUBSTANCE_CATEGORIES.map((c) => [c.value, c.label.split(" — ")[0]])
);

// ── Collapsible-form scaffold (CBAM AddImportForm idiom) ──────────────────────
function FormCard({ Icon, title, count, addLabel, children }) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <Overline Icon={Icon} style={{ marginBottom: 0 }}>{title}{count !== undefined ? ` · ${count}` : ""}</Overline>
        <Btn variant={open ? "ghost" : "secondary"} Icon={open ? X : Plus} onClick={() => setOpen(!open)}>
          {open ? "Cancel" : addLabel}
        </Btn>
      </div>
      {open && children(() => setOpen(false))}
    </Card>
  );
}

const gridForm = {
  display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))",
  gap: 12, alignItems: "end", padding: 16, background: C.bg,
  border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 16,
};

// ── Air emissions form ────────────────────────────────────────────────────────
function AirForm({ reportingPeriod, onSubmit, busy, close }) {
  const [f, setF] = useState(() => ({
    facility_id: "", facility_name: "", measurement_method: "calculated",
    ...Object.fromEntries(AIR_POLLUTANTS.map((p) => [p.key, ""])),
  }));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.facility_id.trim().length > 0;
  return (
    <form
      style={gridForm}
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        const body = {
          facility_id: f.facility_id.trim(),
          facility_name: f.facility_name.trim(),
          reporting_period: reportingPeriod,
          measurement_method: f.measurement_method,
        };
        AIR_POLLUTANTS.forEach((p) => { body[p.key] = Number(f[p.key]) || 0; });
        onSubmit(body).then((ok) => ok && close());
      }}
    >
      <div><Label>Facility ID *</Label><input value={f.facility_id} onChange={set("facility_id")} placeholder="FAC-001" style={field} required /></div>
      <div><Label>Facility name</Label><input value={f.facility_name} onChange={set("facility_name")} placeholder="Plant A" style={field} /></div>
      {AIR_POLLUTANTS.map((p) => (
        <div key={p.key}>
          <Label>{p.label} (kg)</Label>
          <input value={f[p.key]} onChange={set(p.key)} type="number" step="0.001" min="0" placeholder="0" style={field} />
        </div>
      ))}
      <div>
        <Label>Measurement method</Label>
        <select value={f.measurement_method} onChange={set("measurement_method")} style={field}>
          {MEASUREMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <Btn type="submit" disabled={!valid || busy} Icon={Plus}>{busy ? "Recording…" : "Record emissions"}</Btn>
    </form>
  );
}

// ── Water discharge form ──────────────────────────────────────────────────────
function WaterForm({ reportingPeriod, onSubmit, busy, close }) {
  const [f, setF] = useState({
    facility_id: "", facility_name: "", discharge_volume_m3: "",
    bod_concentration_mg_l: "", cod_concentration_mg_l: "",
    discharge_destination: "river", measurement_method: "measured",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.facility_id.trim().length > 0 && Number(f.discharge_volume_m3) >= 0 && f.discharge_volume_m3 !== "" && f.discharge_destination;
  return (
    <form
      style={gridForm}
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSubmit({
          facility_id: f.facility_id.trim(),
          facility_name: f.facility_name.trim(),
          reporting_period: reportingPeriod,
          discharge_volume_m3: Number(f.discharge_volume_m3),
          bod_concentration_mg_l: Number(f.bod_concentration_mg_l) || 0,
          cod_concentration_mg_l: Number(f.cod_concentration_mg_l) || 0,
          discharge_destination: f.discharge_destination,
          measurement_method: f.measurement_method,
        }).then((ok) => ok && close());
      }}
    >
      <div><Label>Facility ID *</Label><input value={f.facility_id} onChange={set("facility_id")} placeholder="FAC-001" style={field} required /></div>
      <div><Label>Facility name</Label><input value={f.facility_name} onChange={set("facility_name")} placeholder="Plant A" style={field} /></div>
      <div><Label>Discharge volume (m³) *</Label><input value={f.discharge_volume_m3} onChange={set("discharge_volume_m3")} type="number" step="0.001" min="0" placeholder="0" style={field} required /></div>
      <div><Label>BOD (mg/L)</Label><input value={f.bod_concentration_mg_l} onChange={set("bod_concentration_mg_l")} type="number" step="0.001" min="0" placeholder="0" style={field} /></div>
      <div><Label>COD (mg/L)</Label><input value={f.cod_concentration_mg_l} onChange={set("cod_concentration_mg_l")} type="number" step="0.001" min="0" placeholder="0" style={field} /></div>
      <div>
        <Label>Discharge destination *</Label>
        <select value={f.discharge_destination} onChange={set("discharge_destination")} style={field} required>
          {DISCHARGE_DESTINATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </div>
      <div>
        <Label>Measurement method</Label>
        <select value={f.measurement_method} onChange={set("measurement_method")} style={field}>
          {MEASUREMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <Btn type="submit" disabled={!valid || busy} Icon={Plus}>{busy ? "Recording…" : "Record discharge"}</Btn>
    </form>
  );
}

// ── Substance form ────────────────────────────────────────────────────────────
function SubstanceForm({ reportingPeriod, onSubmit, busy, close }) {
  const [f, setF] = useState({
    facility_id: "", substance_name: "", cas_number: "", category: "svhc",
    quantity_kg: "", use_purpose: "", substitution_planned: false,
    substitution_timeline: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.facility_id.trim() && f.substance_name.trim() && f.category
    && f.quantity_kg !== "" && Number(f.quantity_kg) >= 0 && f.use_purpose.trim();
  return (
    <form
      style={gridForm}
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSubmit({
          facility_id: f.facility_id.trim(),
          reporting_period: reportingPeriod,
          substance_name: f.substance_name.trim(),
          cas_number: f.cas_number.trim() || null,
          category: f.category,
          quantity_kg: Number(f.quantity_kg),
          use_purpose: f.use_purpose.trim(),
          substitution_planned: f.substitution_planned,
          substitution_timeline: f.substitution_planned ? (f.substitution_timeline.trim() || null) : null,
        }).then((ok) => ok && close());
      }}
    >
      <div><Label>Facility ID *</Label><input value={f.facility_id} onChange={set("facility_id")} placeholder="FAC-001" style={field} required /></div>
      <div><Label>Substance name *</Label><input value={f.substance_name} onChange={set("substance_name")} placeholder="Trichloroethylene" style={field} required /></div>
      <div><Label>CAS number</Label><input value={f.cas_number} onChange={set("cas_number")} placeholder="79-01-6" style={field} /></div>
      <div>
        <Label>Category *</Label>
        <select value={f.category} onChange={set("category")} style={field} required>
          {SUBSTANCE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <div><Label>Quantity (kg) *</Label><input value={f.quantity_kg} onChange={set("quantity_kg")} type="number" step="0.001" min="0" placeholder="0" style={field} required /></div>
      <div><Label>Use purpose *</Label><input value={f.use_purpose} onChange={set("use_purpose")} placeholder="Metal degreasing" style={field} required /></div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 8 }}>
        <input
          id="e2-subst-planned" type="checkbox" checked={f.substitution_planned}
          onChange={(e) => setF({ ...f, substitution_planned: e.target.checked })}
          style={{ width: 16, height: 16, accentColor: C.primary }}
        />
        <label htmlFor="e2-subst-planned" style={{ fontFamily: SANS, fontSize: 12, color: C.body }}>Substitution planned</label>
      </div>
      <div>
        <Label>Substitution timeline</Label>
        <input
          value={f.substitution_timeline} onChange={set("substitution_timeline")}
          placeholder="2027-Q4" disabled={!f.substitution_planned}
          style={{ ...field, opacity: f.substitution_planned ? 1 : 0.45, cursor: f.substitution_planned ? "text" : "not-allowed" }}
        />
      </div>
      <Btn type="submit" disabled={!valid || busy} Icon={Plus}>{busy ? "Recording…" : "Record substance"}</Btn>
    </form>
  );
}

// ── Exceedance list (from compliance_status on each record) ───────────────────
function ExceedancesCard({ air, water }) {
  const rows = [];
  const collect = (recs, unit) =>
    recs.forEach((r) =>
      ((r.compliance_status || {}).exceedances || []).forEach((x) =>
        rows.push({ ...x, facility_id: r.facility_id, unit: x.unit || unit })));
  collect(air, "kg/year");
  collect(water, "mg/L");
  if (rows.length === 0) return null;
  return (
    <Card accent={C.destructive}>
      <Overline Icon={AlertTriangle} color={C.destructive}>Regulatory exceedances · {rows.length}</Overline>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((x, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <Badge c={C.destructive} Icon={ShieldAlert}
              label={`${(x.pollutant || "").toUpperCase()} · ${fmt(x.value, 2)} > ${fmt(x.limit, 2)} ${x.unit}`} />
            <span style={{ fontFamily: MONO, fontSize: 11, color: C.muted }}>
              {x.facility_id} · +{fmt(x.exceedance_percent, 1)}% · {x.reference}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function E2Pollution() {
  const { entities, entityId, setEntityId } = useEntities("esrs_e2_entity_id");
  const [reportingPeriod, setReportingPeriod] = useState("FY2026");
  const [periodDraft, setPeriodDraft] = useState("FY2026");
  const [tab, setTab] = useState("air");

  const [disclosure, setDisclosure] = useState(null);
  const [records, setRecords] = useState({ air: [], water: [], substances: [] });
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    try {
      const qs = `entity_id=${encodeURIComponent(entityId)}&reporting_period=${encodeURIComponent(reportingPeriod)}`;
      const [disc, recs] = await Promise.all([
        apiFetch(`/api/esrs/e2/disclosure?${qs}`),
        apiFetch(`/api/esrs/e2/records?${qs}`),
      ]);
      setDisclosure(disc);
      setRecords({
        air: recs.air || [], water: recs.water || [], substances: recs.substances || [],
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [entityId, reportingPeriod]);

  useEffect(() => { load(); }, [load]);

  // Regulatory limits are a static reference table (no entity/period params) —
  // fetch once per mount rather than on every reload.
  useEffect(() => {
    apiFetch(`/api/esrs/e2/limits`).then(setLimits).catch(() => {});
  }, []);

  const post = async (path, body) => {
    setBusy(true);
    setError("");
    try {
      await apiFetch(path, { method: "POST", body: { ...body, entity_id: entityId } });
      await load();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <PageHeader
      overline="ESRS E2 · Pollution"
      OverlineIcon={Factory}
      title="Pollution"
      subtitle="E2-4 quantifies pollutants released to air and water; E2-5 tracks substances of concern (SVHC/POP/ODS/CMR) and their substitution. Every facility record traces to REACH, the EU IED and UWWTD reference limits."
      entities={entities}
      entityId={entityId}
      onEntityChange={setEntityId}
      onRefresh={load}
      loading={loading}
      actions={
        <input
          value={periodDraft}
          onChange={(e) => setPeriodDraft(e.target.value)}
          onBlur={() => setReportingPeriod(periodDraft)}
          onKeyDown={(e) => { if (e.key === "Enter") setReportingPeriod(periodDraft); }}
          aria-label="Reporting period"
          style={{ ...field, width: 110 }}
        />
      }
    />
  );

  if (!entityId) {
    return (
      <Page>
        {header}
        <Card>
          <EmptyState
            title="Select a reporting entity"
            body="Choose a reporting entity to view its ESRS E2 pollution disclosure, record air emissions and water discharges, and track substances of concern."
          />
        </Card>
      </Page>
    );
  }

  const e24 = disclosure?.esrs_e2_4_disclosure;
  const e25 = disclosure?.esrs_e2_5_disclosure;
  const summary = disclosure?.compliance_summary;
  const overall = summary?.overall_compliant;

  const measurementBadge = (m) => (
    <Badge c={m === "measured" ? C.primary : m === "calculated" ? C.locked : C.neutral} Icon={Scale} label={m} />
  );

  const airRows = records.air;
  const waterRows = records.water;
  const substanceRows = records.substances;

  const tabs = [
    { id: "air", label: "Air emissions", count: records.air.length },
    { id: "water", label: "Water discharges", count: records.water.length },
    { id: "substances", label: "Substances", count: records.substances.length },
    { id: "records", label: "Records" },
  ];

  return (
    <Page>
      {header}
      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {loading && !disclosure ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={110} />
          <Skeleton h={220} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {/* KPI strip */}
          <Card>
            <Overline Icon={Factory}>E2-4 / E2-5 disclosure · {reportingPeriod}</Overline>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 16 }}>
              <Metric label="Total air pollutants" value={fmt(e24?.air_emissions?.total_emissions_kg, 3)} unit="kg" />
              <Metric label="Total discharge volume" value={fmt(e24?.water_discharge?.total_volume_m3, 3)} unit="m³" />
              <Metric label="Substances of concern" value={fmt(e25?.substances_count, 0)} unit="tracked" />
              <div>
                <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 6 }}>Compliance status</div>
                {overall === undefined ? (
                  <Badge c={C.neutral} Icon={ShieldCheck} label="No data" />
                ) : overall ? (
                  <Badge c={C.primary} Icon={ShieldCheck} label="Compliant" />
                ) : (
                  <Badge c={C.destructive} Icon={ShieldAlert} label={`${fmt(summary?.exceedances_count, 0)} exceedance(s)`} />
                )}
                {summary?.limits_authoritative && (
                  <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <DataQualityBadge
                      authoritative={summary.limits_authoritative.air}
                      label={`Air limits ${summary.limits_authoritative.air ? "authoritative" : "illustrative"}`}
                      note={e24?.air_emissions?.compliance_basis?.note || summary.data_quality_caveat}
                    />
                    <DataQualityBadge
                      authoritative={summary.limits_authoritative.water}
                      label={`Water limits ${summary.limits_authoritative.water ? "authoritative" : "illustrative"}`}
                      note={e24?.water_discharge?.compliance_basis?.note}
                    />
                  </div>
                )}
              </div>
            </div>
          </Card>

          <ExceedancesCard air={records.air} water={records.water} />

          <TabBar tabs={tabs} active={tab} onChange={setTab} />

          {/* Air emissions tab */}
          {tab === "air" && (
            <FormCard Icon={Wind} title="Air emissions" count={records.air.length} addLabel="Add record"
              children={(close) => (
                <AirForm reportingPeriod={reportingPeriod} busy={busy} close={close}
                  onSubmit={(b) => post("/api/esrs/e2/air-emissions", b)} />
              )}
            >
              <DataTable
                keyField="record_id"
                emptyLabel="No air emission records for this period"
                columns={[
                  { key: "facility", label: "Facility", render: (r) => (
                    <div><Data>{r.facility_id}</Data>{r.facility_name ? <span style={{ fontFamily: SANS, fontSize: 11, color: C.muted, marginLeft: 6 }}>{r.facility_name}</span> : null}</div>
                  ) },
                  { key: "total", label: "Total (kg)", align: "right", render: (r) => <Data>{fmt(r.total_emissions_kg, 3)}</Data> },
                  { key: "sox", label: "SOx", align: "right", render: (r) => <Data color={C.body}>{fmt(r.emissions_kg?.sox, 2)}</Data> },
                  { key: "nox", label: "NOx", align: "right", render: (r) => <Data color={C.body}>{fmt(r.emissions_kg?.nox, 2)}</Data> },
                  { key: "pm", label: "PM10 / 2.5", align: "right", render: (r) => <Data color={C.body}>{fmt(r.emissions_kg?.pm10, 2)} / {fmt(r.emissions_kg?.pm25, 2)}</Data> },
                  { key: "method", label: "Method", render: (r) => measurementBadge(r.measurement_method) },
                  { key: "compliant", label: "Status", render: (r) => (
                    (r.compliance_status || {}).compliant === false
                      ? <Badge c={C.destructive} Icon={ShieldAlert} label="Exceedance" />
                      : <Badge c={C.primary} Icon={ShieldCheck} label="Compliant" />
                  ) },
                ]}
                rows={airRows}
              />
            </FormCard>
          )}

          {/* Water discharges tab */}
          {tab === "water" && (
            <FormCard Icon={Droplets} title="Water discharges" count={records.water.length} addLabel="Add record"
              children={(close) => (
                <WaterForm reportingPeriod={reportingPeriod} busy={busy} close={close}
                  onSubmit={(b) => post("/api/esrs/e2/water-discharges", b)} />
              )}
            >
              <DataTable
                keyField="record_id"
                emptyLabel="No water discharge records for this period"
                columns={[
                  { key: "facility", label: "Facility", render: (r) => (
                    <div><Data>{r.facility_id}</Data>{r.facility_name ? <span style={{ fontFamily: SANS, fontSize: 11, color: C.muted, marginLeft: 6 }}>{r.facility_name}</span> : null}</div>
                  ) },
                  { key: "volume", label: "Volume (m³)", align: "right", render: (r) => <Data>{fmt(r.discharge_volume_m3, 3)}</Data> },
                  { key: "bod", label: "BOD (mg/L)", align: "right", render: (r) => <Data color={C.body}>{fmt(r.concentrations_mg_l?.bod, 2)}</Data> },
                  { key: "cod", label: "COD (mg/L)", align: "right", render: (r) => <Data color={C.body}>{fmt(r.concentrations_mg_l?.cod, 2)}</Data> },
                  { key: "load", label: "Load (kg)", align: "right", render: (r) => <Data color={C.body}>{fmt(r.total_pollutant_load_kg, 3)}</Data> },
                  { key: "dest", label: "Destination", render: (r) => <Data color={C.body}>{r.discharge_destination}</Data> },
                  { key: "method", label: "Method", render: (r) => measurementBadge(r.measurement_method) },
                  { key: "compliant", label: "Status", render: (r) => (
                    (r.compliance_status || {}).compliant === false
                      ? <Badge c={C.destructive} Icon={ShieldAlert} label="Exceedance" />
                      : <Badge c={C.primary} Icon={ShieldCheck} label="Compliant" />
                  ) },
                ]}
                rows={waterRows}
              />
            </FormCard>
          )}

          {/* Substances tab */}
          {tab === "substances" && (
            <FormCard Icon={FlaskConical} title="Substances of concern" count={records.substances.length} addLabel="Add record"
              children={(close) => (
                <SubstanceForm reportingPeriod={reportingPeriod} busy={busy} close={close}
                  onSubmit={(b) => post("/api/esrs/e2/substances", b)} />
              )}
            >
              <DataTable
                keyField="record_id"
                emptyLabel="No substances of concern for this period"
                columns={[
                  { key: "name", label: "Substance", render: (r) => (
                    <div><Data>{r.substance_name}</Data>{r.cas_number ? <span style={{ fontFamily: MONO, fontSize: 11, color: C.muted, marginLeft: 6 }}>CAS {r.cas_number}</span> : null}</div>
                  ) },
                  { key: "category", label: "Category", render: (r) => <Badge c={C.accent} Icon={FlaskConical} label={CATEGORY_LABEL[r.category] || r.category} /> },
                  { key: "facility", label: "Facility", render: (r) => <Data color={C.body}>{r.facility_id}</Data> },
                  { key: "quantity", label: "Quantity (kg)", align: "right", render: (r) => <Data>{fmt(r.quantity_kg, 3)}</Data> },
                  { key: "use", label: "Use purpose", render: (r) => <span style={{ fontFamily: SANS, fontSize: 12, color: C.body }}>{r.use_purpose}</span> },
                  { key: "subst", label: "Substitution", render: (r) => (
                    r.substitution_planned
                      ? <Badge c={C.primary} Icon={Recycle} label={r.substitution_timeline ? `Planned · ${r.substitution_timeline}` : "Substitution planned"} />
                      : <Badge c={C.neutral} Icon={Ban} label="No substitution plan" />
                  ) },
                ]}
                rows={substanceRows}
              />
            </FormCard>
          )}

          {/* Records tab */}
          {tab === "records" && (
            <div style={{ display: "grid", gap: 16 }}>
              <Card>
                <Overline Icon={ListChecks}>All records · {reportingPeriod}</Overline>
                <DataTable
                  keyField="record_id"
                  emptyLabel="No records for this period"
                  columns={[
                    { key: "type", label: "Type", render: (r) => (
                      <Badge
                        c={r._type === "air" ? C.locked : r._type === "water" ? C.primary : C.accent}
                        Icon={r._type === "air" ? Wind : r._type === "water" ? Droplets : FlaskConical}
                        label={r._type}
                      />
                    ) },
                    { key: "record_id", label: "Record ID", render: (r) => <Data color={C.body}>{r.record_id}</Data> },
                    { key: "facility_id", label: "Facility", render: (r) => <Data color={C.body}>{r.facility_id}</Data> },
                    { key: "detail", label: "Detail", render: (r) => <span style={{ fontFamily: SANS, fontSize: 12, color: C.body }}>{r._detail}</span> },
                    { key: "metric", label: "Value", align: "right", render: (r) => <Data>{r._metric}</Data> },
                  ]}
                  rows={[
                    ...records.air.map((r) => ({ ...r, _type: "air", _detail: `${r.measurement_method} · air emissions`, _metric: `${fmt(r.total_emissions_kg, 3)} kg` })),
                    ...records.water.map((r) => ({ ...r, _type: "water", _detail: `${r.discharge_destination} · ${r.measurement_method}`, _metric: `${fmt(r.discharge_volume_m3, 3)} m³` })),
                    ...records.substances.map((r) => ({ ...r, _type: "substance", _detail: `${(CATEGORY_LABEL[r.category] || r.category)} · ${r.substance_name}`, _metric: `${fmt(r.quantity_kg, 3)} kg` })),
                  ]}
                />
              </Card>

              <Card>
                <Overline Icon={Scale}>Regulatory limits reference</Overline>
                <p style={{ fontFamily: SANS, fontSize: 12, color: C.muted, margin: "0 0 12px", maxWidth: "70ch", lineHeight: 1.5 }}>
                  Example thresholds (EU IED / UWWTD) used by the compliance check. Verify against the operating permit before assurance use.
                </p>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                    <thead>
                      <tr>
                        {["Medium", "Pollutant", "Limit", "Unit", "Reference"].map((h) => (
                          <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontFamily: SANS, fontSize: 11, fontWeight: 600, color: C.muted, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {limits && Object.entries(limits).flatMap(([medium, group]) =>
                        Object.entries(group).map(([pollutant, info]) => (
                          <tr key={`${medium}-${pollutant}`}>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}` }}><Data color={C.body}>{medium}</Data></td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}` }}><Data color={C.body}>{pollutant.toUpperCase()}</Data></td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}` }}><Data>{fmt(info.limit, 2)}</Data></td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}` }}><Data color={C.body}>{info.unit}</Data></td>
                            <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}` }}><Data color={C.body}>{info.reference}</Data></td>
                          </tr>
                        ))
                      )}
                      {!limits && (
                        <tr><td colSpan={5} style={{ padding: "9px 10px", fontFamily: MONO, fontSize: 12, color: C.muted, textAlign: "center" }}>No limits loaded</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
